/**
 * Permissions — mirrors app.role_default_perm / app.has_perm in the database.
 * The database is the source of truth; the UI uses this only to hide what the user can't use.
 */

// worker: field staff who sign in to FarmAI only and have no FarmOS screens
export const ROLES = ['admin', 'sales', 'harvest', 'view', 'worker'] as const;
export type Role = (typeof ROLES)[number];

export const PERM_KEYS = [
  'dash',
  'harvest',
  'sales',
  'purch',
  'tasks',
  'shifts',
  'mgmt',
  'settings',
] as const;
export type PermKey = (typeof PERM_KEYS)[number];

export type Perms = Record<PermKey, boolean> & { admin: boolean };
export type PermOverrides = Partial<Record<PermKey, boolean>>;

export function roleDefaultPerm(role: Role, p: PermKey): boolean {
  switch (role) {
    case 'admin':
      return true;
    case 'harvest':
      return p === 'dash' || p === 'harvest';
    case 'sales':
      return p === 'dash' || p === 'sales' || p === 'purch';
    case 'worker':
      return false;
    default:
      return p === 'dash';
  }
}

export function effectivePerms(role: Role, overrides: PermOverrides = {}): Perms {
  const out = { admin: role === 'admin' } as Perms;
  for (const p of PERM_KEYS) {
    if (role === 'admin') {
      out[p] = true;
      continue;
    }
    const v = overrides[p];
    if (typeof v === 'boolean') out[p] = v;
    else if (p === 'purch' && typeof overrides.sales === 'boolean')
      out[p] = roleDefaultPerm(role, 'purch') || overrides.sales;
    else out[p] = roleDefaultPerm(role, p);
  }
  return out;
}

export const NO_PERMS: Perms = {
  admin: false,
  dash: false,
  harvest: false,
  sales: false,
  purch: false,
  tasks: false,
  shifts: false,
  mgmt: false,
  settings: false,
};
